import { i18n } from '../i18n.js';
import { MoonfallClickGuard } from '../utils/click-guard.js';
import { MoonfallGuest } from '../guest-session.js';
import { MoonfallCloud } from '../convex-client.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
/**
 * UI GameEnd Module
 * Handles game end scenarios (win/lose/disconnect/TTL/inactivity/fatal) and cleanup.
 * Adds an "Open Storybook" button, which opens the match's storybook, on
 * every shareable ending.
 */


import { ActionIntent } from './action-intent.js';
import { isServicePause, servicePauseCopy } from './service-pause.js';
import { CardDrag } from './card-drag.js';
import { Cards } from './cards.js';
import { DeckSelector } from './deck-selector.js';
import { Feedback } from './feedback.js';
import { Indicators } from './indicators.js';
import { Lobby } from './lobby.js';
import { Mulligan } from './mulligan.js';
import { SoundEffects } from './sound-effects.js';
import { SpellAnnounce } from './spell-announce.js';
import { StoryFeed } from './story-feed.js';
import { ThinkingIndicator } from './thinking-indicator.js';
import { TurnTimer } from './turn-timer.js';
import { TurnBanner } from './turn-banner.js';
import { GameState } from '../game-state.js';
import { SocketHandler } from '../socket-handler.js';
let GameEnd = {};

// Lightweight pub/sub context populated by socket-handler.js as we learn the
// storybook info. The results screen's storybook button subscribes, so a
// shortCode that arrives AFTER the screen opens (e.g., via the
// `storybookReady` event) switches the button on where it stands.
// The `|| existing` idempotence wrapper is gone with the global: a module is
// evaluated once per graph, so there is nothing to re-enter.
const MoonfallStorybookContext = (function () {
    const state = {
        shortCode: null,
        failureCode: null,
        ownerCharacter: null,
        opponentCharacter: null,
        mangaEnabled: null,
        /* THE LANGUAGE THE SHARE LINK CARRIES IS THE BOOK'S. A storybook is
           written in the language its match was played in, and the volume
           page prints itself in that language; the link must open it there.
           `i18n.getLanguage()` is the SHARER'S UI language, which differs for
           a PvP joiner or after a post-match switch, and used to mail out a
           French story under a Japanese title. Server-stamped, like the code. */
        language: null,
        /* THE VERSION THE SHARE LINK CARRIES IS THE SERVER'S. It follows the
           card's content -- which panels are in, which are still coming -- and
           is read back from the public storybook API, never invented here: a
           locally minted timestamp gave the same book two different links from
           two surfaces, and could not say whether the card was finished. */
        mediaVersion: null,
        /* null until the server has answered once; then whether any panel is
           still being inked, which is when the card is a placeholder. */
        printing: null,
        /* Bumped when a panel lands on the socket, so an open modal re-polls
           at once instead of waiting for its next tick. */
        mediaNudge: 0,
        roomId: null,
        matchId: null,
    };
    const subscribers = new Set();

    function notify() {
        subscribers.forEach((fn) => {
            try { fn({ ...state }); } catch (_) { /* swallow subscriber errors */ }
        });
    }

    function readScope(source) {
        const gameState = source && source.gameState ? source.gameState : source;
        return {
            roomId: gameState && gameState.roomId ? String(gameState.roomId) : null,
            matchId: gameState && gameState.matchId ? String(gameState.matchId) : null,
        };
    }

    function scopeConflicts(scope) {
        return Boolean(
            (scope.roomId && state.roomId && scope.roomId !== state.roomId)
            || (scope.matchId && state.matchId && scope.matchId !== state.matchId)
        );
    }

    function adoptEventScope(source) {
        const scope = readScope(source);
        if (scopeConflicts(scope)) return false;
        if (scope.roomId && !state.roomId) state.roomId = scope.roomId;
        if (scope.matchId && !state.matchId) state.matchId = scope.matchId;
        return true;
    }

    function activateGameScope(source) {
        const scope = readScope(source);
        if (!scope.roomId && !scope.matchId) return false;
        const changed = scopeConflicts(scope);
        if (changed) {
            state.shortCode = null;
            state.failureCode = null;
            state.ownerCharacter = null;
            state.opponentCharacter = null;
            state.mangaEnabled = null;
            state.language = null;
            state.mediaVersion = null;
            state.printing = null;
        }
        if (scope.roomId) state.roomId = scope.roomId;
        if (scope.matchId) state.matchId = scope.matchId;
        return changed;
    }

    /** The server's `storybookLanguage`, wherever it rides (view, game over, ready). */
    function adoptLanguage(source) {
        const language = source && typeof source.storybookLanguage === 'string'
            ? source.storybookLanguage.trim()
            : '';
        if (!language || state.language === language) return false;
        state.language = language;
        return true;
    }

    return {
        get shortCode() { return state.shortCode; },
        get failureCode() { return state.failureCode; },
        get ownerCharacter() { return state.ownerCharacter; },
        get opponentCharacter() { return state.opponentCharacter; },
        get mangaEnabled() { return state.mangaEnabled; },
        get language() { return state.language; },
        get mediaVersion() { return state.mediaVersion; },
        get printing() { return state.printing; },
        get mediaNudge() { return state.mediaNudge; },
        get roomId() { return state.roomId; },
        get matchId() { return state.matchId; },

        setFromGameState(playerView) {
            if (!playerView) return;
            let changed = activateGameScope(playerView);
            if (playerView.storybookShortCode && state.shortCode !== playerView.storybookShortCode) {
                state.shortCode = playerView.storybookShortCode;
                state.failureCode = null;
                changed = true;
            }
            if (playerView.storybookFailureCode && !state.shortCode
                && state.failureCode !== playerView.storybookFailureCode) {
                state.failureCode = playerView.storybookFailureCode;
                changed = true;
            }
            if (typeof playerView.storybookMangaEnabled === 'boolean'
                && state.mangaEnabled !== playerView.storybookMangaEnabled) {
                state.mangaEnabled = playerView.storybookMangaEnabled;
                changed = true;
            }
            if (adoptLanguage(playerView)) changed = true;
            try {
                const gameState = playerView.gameState || {};
                const me = playerView.player || gameState.player || gameState.players?.[0];
                const them = playerView.opponent || gameState.opponent || gameState.players?.[1];
                const meLabel = me?.name || me?.character;
                const themLabel = them?.name || them?.character;
                if (meLabel && state.ownerCharacter !== meLabel) {
                    state.ownerCharacter = meLabel;
                    changed = true;
                }
                if (themLabel && state.opponentCharacter !== themLabel) {
                    state.opponentCharacter = themLabel;
                    changed = true;
                }
            } catch (_) { /* ignore */ }
            if (changed) notify();
        },
        setShortCode(code, scope) {
            if (!adoptEventScope(scope)) return;
            let changed = false;
            if (typeof scope?.storybookMangaEnabled === 'boolean'
                && state.mangaEnabled !== scope.storybookMangaEnabled) {
                state.mangaEnabled = scope.storybookMangaEnabled;
                changed = true;
            }
            if (adoptLanguage(scope)) changed = true;
            if (code && typeof code === 'string' && state.shortCode !== code) {
                state.shortCode = code;
                state.failureCode = null;
                changed = true;
            }
            if (changed) notify();
        },
        setFailure(code, scope) {
            if (!adoptEventScope(scope) || state.shortCode) return;
            if (code && typeof code === 'string' && state.failureCode !== code) {
                state.failureCode = code;
                notify();
            }
        },
        /** A panel landed on the socket: whoever is polling should ask the server now. */
        markMediaReady(scope) {
            if (!adoptEventScope(scope)) return;
            state.mediaNudge += 1;
            notify();
        },
        /** What the public storybook API said about the book, verbatim. */
        setMediaState(media, scope) {
            if (!adoptEventScope(scope) || !media) return;
            let changed = false;
            const version = Number(media.contentVersion);
            if (Number.isSafeInteger(version) && version >= 0 && state.mediaVersion !== version) {
                state.mediaVersion = version;
                changed = true;
            }
            const printing = media.printing === true;
            if (state.printing !== printing) {
                state.printing = printing;
                changed = true;
            }
            if (changed) notify();
        },
        subscribe(fn) {
            if (typeof fn !== 'function') return () => {};
            subscribers.add(fn);
            return () => subscribers.delete(fn);
        },
        reset() {
            state.shortCode = null;
            state.failureCode = null;
            state.ownerCharacter = null;
            state.opponentCharacter = null;
            state.mangaEnabled = null;
            state.language = null;
            state.mediaVersion = null;
            state.printing = null;
            state.roomId = null;
            state.matchId = null;
            notify();
        },
    };
})();

GameEnd = (function () {
    let gameOverPopup = null;
    let storybookButtonUnsubscribe = null;
    let isCleaningUp = false;
    let resultFocusTimer = null;
    let resultKeyHandler = null;
    let persistentReturnSnapshot = null;
    let rematchGeneration = 0;
    let rematchState = {
        offered: false,
        roomId: null,
        matchId: null,
        excludedCharacter: null,
        submitting: false,
    };
    // When the opponent asks for a rematch first, nudge this player: a short
    // countdown auto-opens their loadout selection so a one-sided "Play Again"
    // doesn't stall on a passive wait. They can still decline (close the
    // selector / return to lobby).
    let rematchCountdownTimer = null;
    const REMATCH_AUTO_ADVANCE_SECONDS = 10;

    function suppressPersistentReturnControl() {
        document.body.classList.add('game-result-active');
        const control = document.getElementById('skip-tutorial-btn');
        if (!control) return;
        if (!persistentReturnSnapshot || persistentReturnSnapshot.element !== control) {
            persistentReturnSnapshot = {
                element: control,
                hidden: control.hidden,
                disabled: control.disabled,
                ariaHidden: control.getAttribute('aria-hidden'),
            };
        }
        control.hidden = true;
        control.disabled = true;
        control.setAttribute('aria-hidden', 'true');
    }

    function restorePersistentReturnControl() {
        document.body.classList.remove('game-result-active');
        const snapshot = persistentReturnSnapshot;
        persistentReturnSnapshot = null;
        if (!snapshot?.element?.isConnected) return;
        snapshot.element.hidden = snapshot.hidden;
        snapshot.element.disabled = snapshot.disabled;
        if (snapshot.ariaHidden === null) snapshot.element.removeAttribute('aria-hidden');
        else snapshot.element.setAttribute('aria-hidden', snapshot.ariaHidden);
    }

    function uiText(key, fallback, vars) {
        const fn = i18n && typeof i18n.t === 'function' ? i18n.t : null;
        const value = fn ? fn(key, vars) : null;
        /* See the note in store.js: the fallback is interpolated too, or a
           cold load can print a literal `{{count}}` to the user. */
        const source = (value && value !== key) ? value : (fallback || key);
        return Object.entries(vars || {}).reduce(
            (text, [name, v]) => String(text)
                .split(`{{${name}}}`).join(String(v))
                .split(`{${name}}`).join(String(v)),
            String(source),
        );
    }

    function storybookFailureText(code) {
        if (code === 'storybook_unavailable') {
            return uiText('share.modal.unavailable', 'There is no story to publish from this match yet.');
        }
        if (code === 'storybook_timeout') {
            return uiText('share.modal.timeout', 'This Storybook is taking too long to prepare. You can return to the lobby; if it finishes, the link will appear here.');
        }
        return uiText('share.modal.failed', 'This Storybook could not be created. You can return to the lobby and keep playing.');
    }

    // Victory celebration intensity knob: how many floating moon motes.
    const MOTE_COUNT = 6;

    function buildEndModal({
        id,
        headerText,
        headerClass,
        messageText,
        sound,
        allowShare,
        allowRematch = false,
        celebrate = false,
    }) {
        try {
            Mulligan?.hide?.({ immediate: true });
        } catch (error) {
            console.error('[GameEnd] failed to clear mulligan before terminal modal:', error);
        }
        try { StoryFeed?.dismissTransient?.({ flush: true, terminal: true }); } catch (_) { /* no-op */ }
        try { StoryFeed?.dismissMangaZoom?.({ restoreFocus: false }); } catch (_) { /* no-op */ }
        try { Cards?.clearHoverPreview?.(); } catch (_) { /* no-op */ }
        try { SpellAnnounce?.dismiss?.(); } catch (_) { /* no-op */ }
        suppressPersistentReturnControl();
        if (gameOverPopup) {
            if (allowRematch) enableRematchOffer();
            return gameOverPopup;
        }

        if (sound && SoundEffects) {
            try { SoundEffects.play(sound); } catch (_) { /* no-op */ }
        }

        gameOverPopup = document.createElement('div');
        gameOverPopup.id = id || 'game-over-popup';
        gameOverPopup.className = 'game-over-popup';
        gameOverPopup.setAttribute('role', 'dialog');
        gameOverPopup.setAttribute('aria-modal', 'true');

        if (celebrate) {
            gameOverPopup.classList.add('game-over-popup--victory');
            for (let i = 0; i < MOTE_COUNT; i++) {
                const mote = document.createElement('span');
                mote.className = 'moon-mote';
                mote.style.setProperty('--mote-i', String(i));
                gameOverPopup.appendChild(mote);
            }
        }

        const content = document.createElement('div');
        content.className = 'game-over-content';

        const header = document.createElement('h2');
        header.textContent = headerText;
        header.className = headerClass;
        header.id = `${gameOverPopup.id}-title`;

        const message = document.createElement('p');
        message.textContent = messageText;
        message.id = `${gameOverPopup.id}-description`;
        gameOverPopup.setAttribute('aria-labelledby', header.id);
        gameOverPopup.setAttribute('aria-describedby', message.id);

        const buttons = document.createElement('div');
        buttons.className = 'game-over-buttons';

        if (allowShare) buttons.appendChild(buildStorybookButton());

        const returnBtn = document.createElement('button');
        returnBtn.type = 'button';
        returnBtn.textContent = uiText('gameEnd.returnToLobby', 'Go to Lobby');
        returnBtn.className = 'return-lobby-btn game-command-btn';
        returnBtn.addEventListener('click', () => {
            const guard = MoonfallClickGuard;
            if (guard && typeof guard.guard === 'function') {
                guard.guard(returnBtn, async () => handleReturnToLobby(), { cooldownMs: 1000 })
                    .catch((err) => console.error('[GameEnd] return guard failed:', err));
            } else {
                handleReturnToLobby();
            }
        });
        buttons.appendChild(returnBtn);

        content.appendChild(header);
        content.appendChild(message);
        content.appendChild(buttons);
        if (allowShare) content.appendChild(buildStorybookNotice());

        gameOverPopup.appendChild(content);
        document.body.appendChild(gameOverPopup);

        installResultFocusTrap();
        if (allowRematch) enableRematchOffer();
        resultFocusTimer = setTimeout(() => {
            const preferred = gameOverPopup?.querySelector('.play-again-btn') || returnBtn;
            preferred?.focus?.();
            resultFocusTimer = null;
        }, 100);
        return gameOverPopup;
    }

    function installResultFocusTrap() {
        if (!gameOverPopup || resultKeyHandler) return;
        resultKeyHandler = (event) => {
            if (event.key !== 'Tab' || !gameOverPopup) return;
            const focusable = [...gameOverPopup.querySelectorAll(
                'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )].filter((element) => !element.hidden);
            if (focusable.length === 0) {
                event.preventDefault();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        gameOverPopup.addEventListener('keydown', resultKeyHandler);
    }

    function resetRematchState() {
        rematchGeneration += 1;
        clearRematchCountdown();
        rematchState = {
            offered: false,
            roomId: null,
            matchId: null,
            excludedCharacter: null,
            submitting: false,
        };
    }

    function clearRematchCountdown() {
        if (rematchCountdownTimer) {
            clearInterval(rematchCountdownTimer);
            rematchCountdownTimer = null;
        }
    }

    // Opponent-requested-first nudge: count down, then auto-open this player's
    // loadout selection. Bails the moment the situation changes (this player
    // acts, the offer is withdrawn, or the modal generation advances).
    function startRematchCountdown() {
        clearRematchCountdown();
        const generation = rematchGeneration;
        let remaining = REMATCH_AUTO_ADVANCE_SECONDS;
        const render = () => setRematchStatus(uiText(
            'gameEnd.rematch.opponentReadyCountdown',
            `Your opponent wants a rematch! Opening your loadout in ${remaining}…`,
            { seconds: remaining }
        ));
        render();
        rematchCountdownTimer = setInterval(() => {
            if (generation !== rematchGeneration || !rematchState.offered || rematchState.submitting) {
                clearRematchCountdown();
                return;
            }
            /* Hold while the player is writing feedback. Both surfaces now
               open on this screen and they share one `lobbySurfaceOwner`, so
               an auto-advance firing underneath the modal would reach
               DeckSelector.choose(), be refused for the lock, and land in
               restoreRematchRetry with no status and no error — the same
               silent nothing this whole change is about. Hold rather than
               cancel: the offer is still good, the player is just busy. */
            try {
                if (Feedback?.isOpen?.()) return;
            } catch (_) { /* a missing module must not stall the countdown */ }
            remaining -= 1;
            if (remaining > 0) {
                render();
                return;
            }
            clearRematchCountdown();
            void chooseRematchLoadout();
        }, 1000);
    }

    function removeResultModal() {
        clearRematchCountdown();
        if (resultFocusTimer) clearTimeout(resultFocusTimer);
        resultFocusTimer = null;
        if (gameOverPopup && resultKeyHandler) {
            gameOverPopup.removeEventListener('keydown', resultKeyHandler);
        }
        resultKeyHandler = null;
        if (gameOverPopup?.parentNode) gameOverPopup.parentNode.removeChild(gameOverPopup);
        gameOverPopup = null;
        if (typeof storybookButtonUnsubscribe === 'function') storybookButtonUnsubscribe();
        storybookButtonUnsubscribe = null;
        restorePersistentReturnControl();
    }

    function rematchScopeMatches(data) {
        return Boolean(
            rematchState.offered
            && data?.roomId === rematchState.roomId
            && data?.matchId === rematchState.matchId
        );
    }

    function setRematchStatus(message) {
        const status = gameOverPopup?.querySelector('.rematch-status');
        if (status) status.textContent = message || '';
    }

    /** @param {{ disabled?: boolean, busy?: boolean }} [state] */
    function setPlayAgainState({ disabled, busy = false } = {}) {
        const button = gameOverPopup?.querySelector('.play-again-btn');
        if (!button) return;
        button.disabled = disabled === true;
        if (busy) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
    }

    function restoreRematchRetry(playAgain, message = null) {
        rematchState.submitting = false;
        setPlayAgainState({ disabled: false, busy: false });
        playAgain?.focus?.();
        if (message) setRematchStatus(message);
    }

    function rematchFailureMessage() {
        return uiText(
            'gameEnd.rematch.failed',
            'The rematch could not be started. Please try again.'
        );
    }

    /* Moonfall paused new matches (servicePause.js): "please try again" would
       be a lie until the pause lifts, so the line says what happened and
       until when, and Play Again stands down. */
    function rematchPauseMessage(payload) {
        return servicePauseCopy(payload, uiText, {
            locale: document.documentElement.lang || undefined,
        }).title;
    }

    function enableRematchOffer() {
        if (!gameOverPopup) return;
        const state = GameState?.get?.() || {};
        if (!state.roomId || !state.matchId) return;
        if (!rematchState.offered) {
            rematchState = {
                offered: true,
                roomId: state.roomId,
                matchId: state.matchId,
                excludedCharacter: null,
                submitting: false,
            };
        }

        const content = gameOverPopup.querySelector('.game-over-content');
        const buttons = gameOverPopup.querySelector('.game-over-buttons');
        if (!content || !buttons) return;
        let status = gameOverPopup.querySelector('.rematch-status');
        if (!status) {
            status = document.createElement('p');
            status.className = 'rematch-status';
            status.setAttribute('role', 'status');
            status.setAttribute('aria-live', 'polite');
            status.setAttribute('aria-atomic', 'true');
            status.textContent = uiText(
                'gameEnd.rematch.chooseLoadout',
                'Choose a new summoner and deck.'
            );
            content.insertBefore(status, buttons);
        }
        if (!gameOverPopup.querySelector('.play-again-btn')) {
            const playAgain = document.createElement('button');
            playAgain.type = 'button';
            playAgain.className = 'play-again-btn';
            playAgain.textContent = uiText('gameEnd.playAgain', 'Play Again');
            playAgain.addEventListener('click', () => { void chooseRematchLoadout(); });
            buttons.insertBefore(playAgain, buttons.firstChild);
        }
    }

    async function chooseRematchLoadout() {
        if (!rematchState.offered || rematchState.submitting) return;
        clearRematchCountdown();
        const generation = rematchGeneration;
        const playAgain = gameOverPopup?.querySelector('.play-again-btn');
        if (!playAgain) return;
        rematchState.submitting = true;
        setPlayAgainState({ disabled: true, busy: true });
        setRematchStatus(uiText(
            'gameEnd.rematch.chooseLoadout',
            'Choose a new summoner and deck.'
        ));

        try {
            while (generation === rematchGeneration && rematchState.offered) {
                let mangaStatus;
                const users = MoonfallCloud?.users;
                if (typeof users?.getMangaGameStatus === 'function') {
                    try {
                        mangaStatus = await users.getMangaGameStatus();
                    } catch (error) {
                        console.warn('[GameEnd] Manga preference status unavailable:', error);
                    }
                }
                const choice = await DeckSelector?.choose?.({
                    excludedCharacter: rematchState.excludedCharacter,
                    mangaStatus,
                    returnFocusTo: playAgain,
                });
                if (generation !== rematchGeneration || !rematchState.offered) return;
                if (!choice || choice.kind !== 'selected') {
                    restoreRematchRetry(playAgain);
                    return;
                }

                gameOverPopup?.querySelector('.return-lobby-btn')?.focus?.();
                setRematchStatus(uiText(
                    'gameEnd.rematch.confirmingLoadout',
                    'Confirming your loadout…'
                ));
                const result = typeof choice.includeManga === 'boolean'
                    ? await SocketHandler?.requestRematch?.(
                        choice.deck,
                        { includeManga: choice.includeManga },
                    )
                    : await SocketHandler?.requestRematch?.(choice.deck);
                if (generation !== rematchGeneration || !rematchState.offered) return;
                if (result?.ok === true) {
                    rematchState.submitting = false;
                    setPlayAgainState({ disabled: true, busy: true });
                    setRematchStatus(result.status === 'starting'
                        ? uiText(
                            'gameEnd.rematch.starting',
                            'Both players are ready. Starting the next match…'
                        )
                        : uiText(
                            'gameEnd.rematch.waitingForOpponent',
                            'Ready — waiting for your opponent.'
                        ));
                    return;
                }
                if (result?.code === 'character_taken') {
                    rematchState.excludedCharacter = result.excludedCharacter
                        || rematchState.excludedCharacter;
                    setRematchStatus(uiText(
                        'gameEnd.rematch.characterTaken',
                        'That summoner was claimed. Choose another.'
                    ));
                    continue;
                }

                if (isServicePause(result)) {
                    rematchState.submitting = false;
                    setPlayAgainState({ disabled: true, busy: false });
                    setRematchStatus(rematchPauseMessage(result));
                    return;
                }

                restoreRematchRetry(playAgain, rematchFailureMessage());
                return;
            }
        } catch (error) {
            console.error('[GameEnd] rematch selection failed:', error?.message || error);
            if (generation !== rematchGeneration) return;
            restoreRematchRetry(playAgain, rematchFailureMessage());
        }
    }

    function updateRematchState(data) {
        if (!rematchScopeMatches(data)) return;
        rematchState.excludedCharacter = data.excludedCharacter || null;
        // The picker may already be open: grey the taken summoner out now
        // rather than after the server refuses it.
        DeckSelector?.setExcludedCharacter?.(rematchState.excludedCharacter);
        if (data.youReady && data.opponentReady) {
            clearRematchCountdown();
            setPlayAgainState({ disabled: true, busy: true });
            setRematchStatus(uiText(
                'gameEnd.rematch.starting',
                'Both players are ready. Starting the next match…'
            ));
        } else if (data.youReady) {
            clearRematchCountdown();
            setPlayAgainState({ disabled: true, busy: true });
            setRematchStatus(uiText(
                'gameEnd.rematch.waitingForOpponent',
                'Ready — waiting for your opponent.'
            ));
        } else if (data.opponentReady) {
            // Opponent asked first: nudge with a countdown that auto-opens this
            // player's loadout selection. Only arm it once (don't restart the
            // clock every time the same state re-broadcasts).
            if (!rematchState.submitting) {
                setPlayAgainState({ disabled: false, busy: false });
                if (!rematchCountdownTimer) startRematchCountdown();
            }
        } else {
            // Neither side ready (opponent withdrew): stand the nudge down.
            clearRematchCountdown();
        }
    }

    function setRematchUnavailable(data) {
        if (!rematchScopeMatches(data)) return;
        const retryable = data.retryable === true;
        rematchState.submitting = false;
        if (!retryable) {
            rematchGeneration += 1;
            rematchState.offered = false;
            DeckSelector?.close?.({
                outcome: { kind: 'cancelled' },
                restoreFocus: false,
            });
        }
        setPlayAgainState({ disabled: !retryable, busy: false });
        let message = rematchFailureMessage();
        if (isServicePause(data)) {
            message = rematchPauseMessage(data);
        } else if (data.reason === 'expired') {
            message = uiText('gameEnd.rematch.expired', 'The rematch offer expired.');
        } else if (data.reason === 'opponent_declined' || data.reason === 'opponent_disconnected') {
            message = uiText('gameEnd.rematch.declined', 'Your opponent returned to the lobby.');
        } else if (data.reason === 'server_updating') {
            /* A release is draining the server: no new battle starts until
               the new version answers (docs/release-process.md, "Step 1"). */
            message = uiText('connection.serverUpdating', 'Moonfall is updating. Your next battle can start in a minute.');
        }
        setRematchStatus(message);
    }

    function showGameOverScreen(isWinner, { canRematch = false } = {}) {
        console.log(`Game over: Player ${isWinner ? 'won' : 'lost'}`);
        buildEndModal({
            id: 'game-over-popup',
            headerText: isWinner ? uiText('gameEnd.victory', 'Victory!') : uiText('gameEnd.defeat', 'Defeat'),
            headerClass: isWinner ? 'victory-header' : 'defeat-header',
            messageText: isWinner
                ? uiText('gameEnd.victoryMessage', 'You have defeated your opponent!')
                : uiText('gameEnd.defeatMessage', 'Your opponent has prevailed. Better luck next time.'),
            sound: isWinner ? 'win' : 'lose',
            allowShare: true,
            allowRematch: canRematch === true,
            celebrate: isWinner,
        });
    }

    function showSessionTTLDisconnect() {
        console.log('Session TTL disconnect – showing TTL popup');
        buildEndModal({
            id: 'game-over-popup',
            headerText: uiText('gameEnd.sessionTTL.title', 'Session Time Limit Reached'),
            headerClass: 'warning-header',
            messageText: uiText('gameEnd.sessionTTL.message', 'Your session has reached its time limit and you have been disconnected.'),
            sound: 'disconnect',
            allowShare: true,
        });
    }

    // `allowShare` is false when the disconnect happened outside a match: there
    // is no storybook to share, and the button was landing dead.
    function showInactivityDisconnect({ allowShare = true } = {}) {
        console.log('Inactivity disconnect – showing inactivity popup');
        buildEndModal({
            id: 'game-over-popup',
            headerText: uiText('gameEnd.inactivity.title', 'Disconnected Due to Inactivity'),
            headerClass: 'warning-header',
            messageText: uiText('gameEnd.inactivity.message', 'You were inactive for too long and have been disconnected.'),
            sound: 'disconnect',
            allowShare,
        });
    }

    function showOpponentDisconnected() {
        console.log('Opponent disconnected – showing disconnect popup');
        buildEndModal({
            id: 'game-over-popup',
            headerText: uiText('gameEnd.opponentDisconnected.title', 'Opponent Disconnected :('),
            headerClass: 'warning-header',
            messageText: uiText('gameEnd.opponentDisconnected.message', 'Your opponent left the match. The game has ended.'),
            sound: 'disconnect',
            allowShare: true,
        });
    }

    function showFatalError(msg) {
        console.error('Fatal error – showing fatal error popup');
        buildEndModal({
            id: 'fatal-error-popup',
            headerText: uiText('gameEnd.fatalError.title', 'Fatal Error'),
            headerClass: 'warning-header',
            messageText: msg || uiText('gameEnd.fatalError.message', 'A critical AI service error occurred. Please refresh the page.'),
            sound: 'disconnect',
            allowShare: false, // errors are not shareable
        });
    }

    // --- The match's storybook ---------------------------------------------

    /** The public link for the finished match, or null while it is being made. */
    function storybookShareLink(context) {
        const shortCode = context?.shortCode;
        if (!shortCode) return null;
        const language = context.language || i18n?.getLanguage?.() || 'en';
        const build = window.MoonfallStorybookUrl?.buildStorybookUrl;
        const options = context.mediaVersion != null
            ? { language, version: context.mediaVersion }
            : { language };
        return build?.(shortCode, options)
            || `${window.location.origin}/s/${encodeURIComponent(shortCode)}`;
    }

    /*
     * OPEN STORYBOOK: the results screen's way into the match's storybook.
     *
     * Asked for as "on the win game screen there can be a button saying
     * 'Read...' and from there sharing is natural". The book is the reward,
     * and its own page carries the one way to share on each kind of device
     * (share-buttons.js), so this button opens the book instead of sharing
     * blind, with no screen in between.
     *
     * The book is made in the seconds after the match ends. Until its link
     * exists the button is shown but unavailable, and it becomes available
     * where it stands the moment the link lands: the label never changes and
     * nothing on the card moves. A book the server could not make leaves it
     * unavailable, with the reason on its title for hover and assistive tech.
     */
    function buildStorybookButton() {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'game-command-btn game-over-share-btn';
        button.textContent = uiText('gameEnd.relive', 'Open Storybook');
        button.setAttribute('aria-describedby', STORYBOOK_NOTICE_ID);
        const sync = () => {
            const context = MoonfallStorybookContext;
            const ready = Boolean(storybookShareLink(context));
            const failed = !ready && Boolean(context?.failureCode);
            button.disabled = !ready;
            button.setAttribute('aria-busy', String(!ready && !failed));
            if (ready) {
                button.removeAttribute('title');
            } else {
                button.title = failed
                    ? storybookFailureText(context.failureCode)
                    : uiText('share.modal.preparing', 'Preparing your storybook…');
            }
        };
        sync();
        if (typeof storybookButtonUnsubscribe === 'function') storybookButtonUnsubscribe();
        storybookButtonUnsubscribe = MoonfallStorybookContext?.subscribe?.(sync) || null;
        button.addEventListener('click', () => openStorybook());
        return button;
    }

    /* THE BOOK IS A PUBLIC PAGE, and this is the last word the player reads
       before opening it. The share sheet used to carry this notice; it moved
       here with the button, present from the first paint so nothing moves
       (tests/unit/marketing/legalClaimsDefensibility.test.js pins its text). */
    const STORYBOOK_NOTICE_ID = 'game-over-storybook-note';

    function buildStorybookNotice() {
        const note = document.createElement('p');
        note.id = STORYBOOK_NOTICE_ID;
        note.className = 'game-over-storybook-note';
        note.textContent = uiText(
            'share.modal.privacy',
            'Your Storybook is a public page. Review it before sharing.',
        );
        return note;
    }

    /* A new tab keeps the results card (and a pending Play Again) where it is.
       Where the browser refuses one, the book opens here instead: the press
       must always arrive somewhere. */
    function openStorybook() {
        const url = storybookShareLink(MoonfallStorybookContext);
        if (!url) return;
        try {
            MoonfallAnalytics?.capture?.('storybook_opened', {
                surface: 'game_end',
                language: MoonfallStorybookContext.language || i18n?.getLanguage?.() || 'en',
            });
        } catch (_) {
            // Analytics is observational and must never consume the press.
        }
        let opened = null;
        try {
            opened = window.open(url, '_blank');
        } catch (_) {
            opened = null;
        }
        if (opened) {
            try { opened.opener = null; } catch (_) { /* same-origin book; hygiene only */ }
            return;
        }
        window.location.assign(url);
    }

    // --- Cleanup --------------------------------------------------------

    function handleReturnToLobby() {
        if (isCleaningUp) return;
        isCleaningUp = true;
        console.log('Cleaning up game state and returning to lobby');

        const offeredScope = rematchState.offered
            ? { roomId: rematchState.roomId, matchId: rematchState.matchId }
            : null;
        if (offeredScope?.roomId && offeredScope?.matchId
            && typeof SocketHandler?.cancelRematch === 'function') {
            void cancelRematchThenReturn(offeredScope);
            return;
        }

        finishReturnToLobby();
    }

    async function cancelRematchThenReturn(offeredScope) {
        let timeoutId = null;
        let cancellationResult = null;
        try {
            const cancellation = SocketHandler.cancelRematch();
            cancellationResult = await Promise.race([
                Promise.resolve(cancellation),
                new Promise((resolve) => {
                    timeoutId = setTimeout(resolve, 750);
                }),
            ]);
        } catch (_) {
            // Leaving remains available when cancellation could not be acknowledged.
        } finally {
            if (timeoutId) clearTimeout(timeoutId);
        }

        if (cancellationResult?.code === 'rematch_starting') {
            // The server has already fenced the old match while it installs the
            // fresh authoritative scope. Let that transition finish instead of
            // disconnecting the socket out from under it.
            isCleaningUp = false;
            return;
        }

        const current = GameState?.get?.() || {};
        if (current.roomId !== offeredScope.roomId || current.matchId !== offeredScope.matchId) {
            // The server won the race and installed the fresh match. A stale Return
            // continuation must never disconnect that new authoritative scope.
            isCleaningUp = false;
            return;
        }
        finishReturnToLobby();
    }

    function finishReturnToLobby() {
        console.log('Finishing game cleanup and returning to lobby');

        try {
            removeResultModal();
            resetRematchState();

            cleanUpGameState({ leaveRoom: true });

            // A guest who finishes the tutorial game has no lobby — route them to the
            // sign-in moment instead ("Keep your cards. Save your story.").
            if (MoonfallGuest && MoonfallGuest.isActive()) {
                MoonfallGuest.endGuestTutorial('complete');
                isCleaningUp = false;
                return;
            }

            if (Lobby) {
                Lobby.switchToLobbyView();
            } else {
                console.error('Lobby module not found');
                const lobbyView = document.getElementById('lobby-view');
                const gameView = document.getElementById('game-view');
                if (lobbyView && gameView) {
                    gameView.classList.add('hidden');
                    lobbyView.classList.remove('hidden');
                }
            }

            isCleaningUp = false;
        } catch (e) {
            console.error('Error cleaning up game state:', e);
            alert(uiText('gameEnd.returnLobbyError', 'Error returning to lobby. Please refresh the page.'));
            isCleaningUp = false;
        }
    }

    function prepareForNextMatch({ socketTrackingAlreadyReset = false } = {}) {
        removeResultModal();
        resetRematchState();
        cleanUpGameState({
            leaveRoom: false,
            resetSocketTracking: !socketTrackingAlreadyReset,
        });
        // If Return was waiting on a cancellation acknowledgement, the fresh
        // rematch transition now owns the client and its continuation must stand down.
        isCleaningUp = false;
    }

    function cleanUpGameState({ leaveRoom = false, resetSocketTracking = true } = {}) {
        try { SoundEffects?.stopAll?.(); } catch (_) { }
        const runStep = (label, step) => {
            try { step(); } catch (error) {
                console.error(`[GameEnd] ${label} cleanup failed:`, error);
            }
        };

        runStep('action state', () => GameState?.setActionInProgress?.(false));
        let state = {};
        runStep('state snapshot', () => { state = GameState?.get?.() || {}; });
        const connectionData = {
            roomId: state.roomId || null,
            playerId: state.playerId || null,
            isHost: state.isHost || false,
            opponentId: state.opponentId || null,
            ready: false,
        };
        console.log('Preserving connection data before cleanup:', {
            roomId: connectionData.roomId,
            playerId: connectionData.playerId,
            isHost: connectionData.isHost,
        });

        [
            /* `restoreFocus: false` because removeResultModal() has already
               detached the popup by the time this runs, so the button the
               modal captured focus from is a disconnected node — restoring to
               it drops focus on <body> instead of leaving it where the next
               screen wants it. */
            ['feedback', () => Feedback?.close?.({ restoreFocus: false })],
            ['mulligan', () => Mulligan?.hide?.({ immediate: true })],
            ['mulligan waiting banner', () => Mulligan?.hideWaitingBanner?.()],
            ['deck selector', () => DeckSelector?.close?.({
                outcome: { kind: 'cancelled' },
                restoreFocus: false,
            })],
            ['action intent', () => ActionIntent?.clear?.()],
            ['spell announce', () => SpellAnnounce?.dismiss?.()],
            ['thinking indicator', () => ThinkingIndicator?.reset?.()],
            ['turn timer', () => TurnTimer?.reset?.()],
            ['status indicators', () => Indicators?.clearStatusIndicators?.()],
            ['card drag', () => CardDrag?.reset?.()],
            ['card tracking', () => Cards?.resetMatchTracking?.()],
            ['turn banner', () => TurnBanner?.reset?.()],
            ['story feed', () => StoryFeed?.reset?.()],
        ].forEach(([label, step]) => runStep(label, step));

        if (leaveRoom) {
            runStep('room departure', () => {
                if (connectionData.roomId) {
                    SocketHandler?.leaveRoom?.(connectionData.roomId);
                    return;
                }
                SocketHandler?.leaveCurrentRoom?.();
            });
        }
        if (resetSocketTracking) {
            runStep('socket tracking', () => SocketHandler?.resetMatchTracking?.());
        }
        runStep('game state', () => GameState?.reset?.());

        runStep('card classes', () => {
            document.querySelectorAll('.dragging, .valid-target, .summoned-this-turn').forEach(el => {
                el.classList.remove('dragging', 'valid-target', 'summoned-this-turn');
            });
        });
        runStep('board nodes', () => {
            ['player-board', 'opponent-board', 'player-hand', 'opponent-hand'].forEach((id) => {
                document.getElementById(id)?.replaceChildren();
            });
        });
        runStep('turn labels', () => {
            const turnNumber = document.getElementById('turn-number');
            const activePlayer = document.getElementById('active-player');
            if (turnNumber) turnNumber.textContent = uiText('game.turnLabel1', 'Turn 1');
            if (activePlayer) activePlayer.textContent = uiText('game.yourTurn', 'Your Turn');
        });
        runStep('storybook context', () => MoonfallStorybookContext?.reset?.());
        runStep('state persistence', () => GameState?.forcePersist?.());
        console.log('Game state cleaned up successfully. Room ID (if any) was cleared.');
    }

    return {
        showGameOverScreen,
        showOpponentDisconnected,
        showFatalError,
        showInactivityDisconnect,
        showSessionTTLDisconnect,
        prepareForNextMatch,
        updateRematchState,
        setRematchUnavailable,
    };
})();

export { GameEnd };

export { MoonfallStorybookContext };
